"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.disambiguatedLabel = exports.baseNodeLabel = exports.compareDisambiguationKeys = exports.disambiguationKey = void 0;
exports.assignNodeLabels = assignNodeLabels;
exports.mobileNodeLacksOrderingKey = mobileNodeLacksOrderingKey;
const generateMobileNodeLabel_1 = require("./generateMobileNodeLabel");
const generateNodeLabel_1 = require("./generateNodeLabel");
/**
 * Whole-session pass over deduplicated results. For every unique node it
 * (1) generates a deterministic base label via `generateNodeLabel`, then
 * (2) disambiguates collisions: when two or more nodes share a non-empty base
 * label, each gets a numeric suffix (`footer a 1`, `footer a 2`) assigned in a
 * deterministic order so the result is stable run-to-run.
 *
 * Mutates `nodeLabel` on each unique node in place. Must be called before
 * `mergeDedupedPageStates`: that merge carries the labels into the merged map
 * either by `structuredClone` (the seed page state per rule) or by reference
 * (subsequent page states, whose unique nodes are pushed in as-is), so labels
 * set here survive both carry paths.
 */
function assignNodeLabels(dedupedResults) {
    // 1. Base label for every unique node across all page states + rules,
    // grouped by non-empty label in the same pass. `nodeLabel` is set on every
    // node (including `undefined` for unparseable HTML); only nodes with a
    // non-empty label enter `groups`.
    const groups = new Map();
    for (const pageState of dedupedResults) {
        for (const ruleResult of pageState.result) {
            for (const node of ruleResult.uniques) {
                const label = (0, exports.baseNodeLabel)(node);
                node.nodeLabel = label;
                if (!label) {
                    continue;
                }
                const group = groups.get(label);
                if (group) {
                    group.push(node);
                }
                else {
                    groups.set(label, [node]);
                }
            }
        }
    }
    // 2. Suffix collision groups in a deterministic order. The suffix width is
    // reserved within MAX_LENGTH (using the widest suffix in the group) and the
    // base is truncated to fit, so a disambiguated label never exceeds the same
    // MAX_LENGTH bound `generateNodeLabel` enforces on the base.
    for (const [label, group] of groups) {
        if (group.length < 2) {
            continue;
        }
        // Without an ordering key the suffix would come from transient list order
        // and could flip between scans; leaving the group's labels identical is at
        // least deterministic run-to-run.
        if (group.some(mobileNodeLacksOrderingKey)) {
            continue;
        }
        group.sort(compareNodesForDisambiguation);
        group.forEach((node, index) => {
            node.nodeLabel = (0, exports.disambiguatedLabel)(label, group.length, index);
        });
    }
}
/**
 * Truncate a base label to `maxLength` at a word boundary, dropping the trailing
 * partial word (and any separator left dangling by the cut). Delegates to the
 * same word-boundary `truncate` `generateNodeLabel` uses to bound the base, so a
 * disambiguated label truncates identically to a non-disambiguated one.
 */
function truncateBase(label, maxLength) {
    if (label.length <= maxLength) {
        return label;
    }
    // e.g. a base of "section a · " cut mid-separator → strip the dangling " · ".
    return (0, generateNodeLabel_1.truncate)(label, maxLength).replace(/\s*·$/, '').trimEnd();
}
/**
 * A mobile node carries no xpath, and `watcherDedupe` computes `fingerprints`
 * only for a node whose `target` is non-empty — so for these nodes every tier
 * of `compareNodesForDisambiguation` is identical across the group and the sort
 * cannot order them.
 */
function mobileNodeLacksOrderingKey(node) {
    return Boolean(node.mobileLabelInput) && !node.target?.length;
}
/** Stable string key for a selector-like array (target/xpath). */
function selectorKey(selector) {
    return selector ? JSON.stringify(selector) : '';
}
/**
 * Locale-independent string comparison by code point, so collision ordering is
 * stable regardless of the runtime's ICU locale (unlike `localeCompare`).
 */
function compareStrings(a, b) {
    if (a < b) {
        return -1;
    }
    if (a > b) {
        return 1;
    }
    return 0;
}
const disambiguationKey = (node) => ({
    xpath: selectorKey(node.xpath),
    selector: selectorKey(node.target),
    fingerprint: node.fingerprints?.[0] ?? ''
});
exports.disambiguationKey = disambiguationKey;
const compareDisambiguationKeys = (a, b) => compareStrings(a.xpath, b.xpath) ||
    compareStrings(a.selector, b.selector) ||
    compareStrings(a.fingerprint, b.fingerprint);
exports.compareDisambiguationKeys = compareDisambiguationKeys;
/**
 * Deterministic ordering for collision groups: xpath, then selector (target),
 * then primary fingerprint. All three keys derive from identifying properties,
 * so the order is stable across runs of the same scan. The fingerprint tier is
 * a last-resort tie-break — `fingerprints[0]` is a deterministic hash of the
 * node's rule/url/target, reproducible per run for identical input.
 */
function compareNodesForDisambiguation(a, b) {
    return (0, exports.compareDisambiguationKeys)((0, exports.disambiguationKey)(a), (0, exports.disambiguationKey)(b));
}
/** `undefined` for unparseable HTML, so the field has a single "no label" representation. */
const baseNodeLabel = (node) => (node.mobileLabelInput
    ? (0, generateMobileNodeLabel_1.generateMobileNodeLabel)(node.mobileLabelInput)
    : (0, generateNodeLabel_1.generateNodeLabel)({
        html: node.html,
        selector: node.target,
        xpath: node.xpath,
        ancestry: node.ancestry
    })) || undefined;
exports.baseNodeLabel = baseNodeLabel;
/**
 * The suffix width is reserved within MAX_LENGTH (using the widest suffix in
 * the group) and the base is truncated to fit.
 */
const disambiguatedLabel = (base, groupSize, index) => `${truncateBase(base, generateNodeLabel_1.MAX_LENGTH - ` ${groupSize}`.length)} ${index + 1}`;
exports.disambiguatedLabel = disambiguatedLabel;
//# sourceMappingURL=assignNodeLabels.js.map